import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { usePerformanceMonitor } from '@/hooks/usePerformanceMonitor';
import { useErrorCapture } from '@/hooks/useErrorCapture';

interface PerformanceContextValue {
  sessionId: string | null;
  isActive: boolean;
  captureError: (error: Error, componentStack?: string) => void;
}

const PerformanceContext = createContext<PerformanceContextValue | null>(null);

export function PerformanceProvider({ children }: { children: React.ReactNode }) {
  const { data: appUser } = useCurrentAppUser();
  const location = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get or create performance session
  useEffect(() => {
    if (!appUser?.id) return;

    const getOrCreateSession = async () => {
      // Check for existing active session
      const { data: existingSession } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('user_id', appUser.id)
        .eq('is_active', true)
        .order('session_started_at', { ascending: false })
        .limit(1)
        .single();

      if (existingSession?.id) {
        setSessionId(existingSession.id);
      }
    };

    getOrCreateSession();
  }, [appUser?.id]);

  // Initialize performance monitoring with session
  usePerformanceMonitor(sessionId);
  
  // Initialize error capture with session
  const { captureError } = useErrorCapture(sessionId);

  // Track activity state
  useEffect(() => {
    const handleActivity = () => {
      setIsActive(true);
    };

    const handleIdle = () => {
      setIsActive(false);
    };

    let idleTimeout: NodeJS.Timeout;
    
    const resetIdleTimer = () => {
      clearTimeout(idleTimeout);
      handleActivity();
      idleTimeout = setTimeout(handleIdle, 5 * 60 * 1000); // 5 minutes
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, resetIdleTimer, { passive: true });
    });

    resetIdleTimer();

    return () => {
      clearTimeout(idleTimeout);
      events.forEach(event => {
        window.removeEventListener(event, resetIdleTimer);
      });
    };
  }, []);

  return (
    <PerformanceContext.Provider value={{ sessionId, isActive, captureError }}>
      {children}
    </PerformanceContext.Provider>
  );
}

export const usePerformanceContext = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    return { sessionId: null, isActive: true, captureError: () => {} };
  }
  return context;
};
